import React, { useState, useRef } from "react";
import api from "@/lib/api";
import { sendPhoneCode, confirmPhoneCode, firebaseErrorMessage } from "@/lib/firebase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, MessageSquare, Loader2, Truck, User, ArrowLeft } from "lucide-react";

export default function OtpAuth({ role, title, subtitle, prefillChannel, prefillIdentifier }) {
  const { setUser } = useAuth();
  const [channel, setChannel] = useState(prefillChannel === "email" ? "email" : "sms");
  const [identifier, setIdentifier] = useState(prefillIdentifier || "");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const confirmationRef = useRef(null);
  const phone10 = identifier.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "").replace(/^0/, "");

  const requestOtp = async () => {
    if (!identifier) return toast.error(channel === "email" ? "Enter your email" : "Enter your phone");
    if (channel === "sms" && phone10.length !== 10) return toast.error("Enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      if (channel === "sms") confirmationRef.current = await sendPhoneCode(phone10, "recaptcha-container");
      else await api.post("/auth/otp/request", { identifier, channel, role });
      toast.success(`Code sent via ${channel === "email" ? "email" : "SMS"}`);
      setStep(2);
    } catch (e) {
      toast.error(channel === "sms" ? firebaseErrorMessage(e) : (e?.response?.data?.detail || "Could not send code"));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (!code) return toast.error("Enter the code");
    setBusy(true);
    try {
      let res;
      if (channel === "sms") {
        const idToken = await confirmPhoneCode(confirmationRef.current, code);
        res = await api.post("/auth/firebase", { id_token: idToken, role, name });
      } else {
        res = await api.post("/auth/otp/verify", { identifier, channel, role, code, name });
      }
      setUser(res.data.user);
      toast.success("Verified!");
    } catch (e) {
      toast.error(e?.code ? firebaseErrorMessage(e) : (e?.response?.data?.detail || "Invalid code"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
            <Truck className="w-6 h-6 text-slate-900" />
          </div>
          <span className="font-display text-xl font-extrabold text-slate-900">RouteWheels</span>
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900">{title}</h1>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>

        {step === 1 ? (
          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="otp-channel-email"
                onClick={() => setChannel("email")}
                className={`flex items-center justify-center gap-2 h-11 rounded-xl border font-medium text-sm ${channel === "email" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600"}`}
              >
                <Mail className="w-4 h-4" /> Email
              </button>
              <button
                data-testid="otp-channel-sms"
                onClick={() => setChannel("sms")}
                className={`flex items-center justify-center gap-2 h-11 rounded-xl border font-medium text-sm ${channel === "sms" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-600"}`}
              >
                <MessageSquare className="w-4 h-4" /> SMS
              </button>
            </div>
            <div>
              <Label className="text-sm">Your Name</Label>
              <Input data-testid="otp-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-sm">{channel === "email" ? "Email Address" : "Phone Number"}</Label>
              {channel === "email" ? (
                <Input data-testid="otp-identifier-input" type="email" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@example.com" className="mt-1.5" />
              ) : (
                <div className="mt-1.5 flex items-center rounded-md border border-input px-3">
                  <span className="text-slate-500 text-sm mr-2">+91</span>
                  <Input data-testid="otp-identifier-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="98765 43210" className="border-0 shadow-none focus-visible:ring-0 px-0" />
                </div>
              )}
            </div>
            <Button data-testid="otp-request-button" onClick={requestOtp} disabled={busy} className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Code"}
            </Button>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
              <ArrowLeft className="w-4 h-4" /> Change {channel === "email" ? "email" : "number"}
            </button>
            <div>
              <Label className="text-sm">Enter 6-digit code sent to {identifier}</Label>
              <Input data-testid="otp-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="______" maxLength={6} className="mt-1.5 text-center text-2xl tracking-[0.5em] font-bold" />
            </div>
            <Button data-testid="otp-verify-button" onClick={verifyOtp} disabled={busy} className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Verify & Continue"}
            </Button>
            <button data-testid="otp-resend" onClick={requestOtp} className="w-full text-sm text-amber-600 font-medium">Resend code</button>
          </div>
        )}
        <div id="recaptcha-container" />
      </div>
    </div>
  );
}
